import { useState } from 'react';
import { isNil } from 'es-toolkit';
import { toast } from 'sonner';
import { useActiveHouseholdId } from 'src/hooks/use-active-household-id';
import { useConnectNudgeDismissal } from 'src/hooks/use-connect-nudge-dismissal';
import { useHouseholdWriteAccess } from 'src/hooks/use-household-write-access';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import type { AccountPeriodReturns } from '../account-performance';
import type { AccountGroupId, AddAccountItemType } from '../constants';
import type { PortfolioHistoryGroupInput } from '../net-worth';
import type { StalenessDismissals } from '../staleness-dismissal';
import type { AccountGroup, AccountRow, StaleConnection, StaleConnectionReason } from '../types';
import { ClientPortalAccountsFreshnessQuery } from '../../../api/accounts-freshness';
import { ClientPortalAccountsListQuery } from '../../../api/accounts-list';
import {
  ClientPortalDeleteAccountMutation,
  ClientPortalDeleteAssetMutation,
  ClientPortalDeleteLiabilityMutation,
  ClientPortalUpdateAccountCustomNameMutation,
  ClientPortalUpdateAccountNetWorthMutation,
  ClientPortalUpdateAssetNetWorthMutation,
  ClientPortalUpdateLiabilityNetWorthMutation,
} from '../../../api/accounts-mutations';
import { ClientPortalNetWorthHistoryQuery } from '../../../api/net-worth-history';
import { useClientPortalFundingPhase } from '../../../hooks/use-client-portal-funding-phase';
import { useClientPortalNav } from '../../shell/use-client-portal-nav';
import {
  ACCOUNTS_BALANCE_UPDATED_TOAST,
  ACCOUNTS_HIDDEN_FROM_NET_WORTH_TOAST,
  ACCOUNTS_INCLUDED_IN_NET_WORTH_TOAST,
  ACCOUNTS_NET_WORTH_ERROR_TOAST,
  ACCOUNTS_REFRESHED_TOAST,
  ACCOUNTS_REMOVE_ERROR_TOAST,
  ACCOUNTS_REMOVED_TOAST,
  ACCOUNTS_RENAME_CLEARED_TOAST,
  ACCOUNTS_RENAME_ERROR_TOAST,
  ACCOUNTS_RENAMED_TOAST,
} from '../constants';
import { applyRowOrder } from '../row-order';
import { dismissalsWith, readDismissals, undismissedConnections, writeDismissals } from '../staleness-dismissal';
import {
  accountGroups,
  balancesUpdatedAt,
  balancesUpdatedLabel,
  netWorthFromGroups,
  shouldShowConnectNudge,
  staleConnections,
} from '../utils';
import { useAccountRowOrder } from './use-account-row-order';
import { useNetWorthPerformance } from './use-net-worth-performance';

export type ClientPortalAccountsState = {
  readonly loading: boolean;
  readonly failed: boolean;
  readonly retry: () => void;
  readonly groups: readonly AccountGroup[];
  readonly netWorth: number;
  readonly balancesUpdatedLabel: string | null;
  readonly historyGroups: readonly PortfolioHistoryGroupInput[] | undefined;
  readonly historyLoading: boolean;
  readonly historyFailed: boolean;
  readonly retryHistory: () => void;
  readonly netWorthReturns: AccountPeriodReturns;
  readonly netWorthReturnsLoading: boolean;
  readonly canConnect: boolean;
  readonly connectItemType: AddAccountItemType | undefined;
  readonly connectFeedOnly: boolean;
  readonly connectOpen: boolean;
  readonly openConnect: (itemType?: AddAccountItemType, feedOnly?: boolean) => void;
  readonly closeConnect: () => void;
  readonly onConnected: () => Promise<unknown>;
  readonly canManageRows: boolean;
  readonly dropRowOnto: (groupId: AccountGroupId, rowIds: readonly string[], movedId: string, targetId: string) => void;
  readonly nudgeRow: (groupId: AccountGroupId, rowIds: readonly string[], movedId: string, offset: number) => void;
  readonly refreshBalances: () => void;
  readonly renamingRow: AccountRow | null;
  readonly pendingCustomNameId: string | null;
  readonly openRename: (row: AccountRow) => void;
  readonly closeRename: () => void;
  readonly renameRow: (row: AccountRow, customName: string | null) => Promise<void>;
  readonly balanceRow: AccountRow | null;
  readonly openBalanceUpdate: (row: AccountRow) => void;
  readonly closeBalanceUpdate: () => void;
  readonly onBalanceUpdated: () => void;
  readonly removeRow: (row: AccountRow) => Promise<void>;
  readonly toggleRowInNetWorth: (row: AccountRow) => Promise<void>;
  readonly staleConnections: readonly StaleConnection[];
  readonly dismissStaleConnections: (reason: StaleConnectionReason) => void;
  readonly onReconnected: () => Promise<unknown>;
  readonly transferring: boolean;
  readonly destinationCustodian: string | null;
  readonly showConnectNudge: boolean;
  readonly dismissConnectNudge: () => void;
};

export function useClientPortalAccounts(): ClientPortalAccountsState {
  const { user } = useAuth();
  const { activeHouseholdId, canQuery } = useActiveHouseholdId();
  const { canWrite } = useHouseholdWriteAccess();
  const { transferring, destinationCustodian, phase } = useClientPortalFundingPhase();
  const { personalPortfolioId, loading: navLoading } = useClientPortalNav();
  const awaitsFirstOrionSnapshot = phase === 'AWAITING_ACCOUNT_OPENING';
  const { returns: netWorthReturns, loading: netWorthReturnsLoading } = useNetWorthPerformance(
    personalPortfolioId ?? undefined,
    !navLoading && !awaitsFirstOrionSnapshot,
  );
  const { isDismissed: connectNudgeDismissed, dismiss: dismissConnectNudge } = useConnectNudgeDismissal();
  const [connectItemType, setConnectItemType] = useState<AddAccountItemType | undefined>(undefined);
  const [connectFeedOnly, setConnectFeedOnly] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [dismissals, setDismissals] = useState<StalenessDismissals>(readDismissals);
  const [renamingRow, setRenamingRow] = useState<AccountRow | null>(null);
  const [pendingCustomNameId, setPendingCustomNameId] = useState<string | null>(null);
  const [balanceRow, setBalanceRow] = useState<AccountRow | null>(null);

  const queryVariables = { id: user?.id ?? '', householdId: activeHouseholdId ?? undefined };
  const skipReads = isNil(user?.id) || !canQuery;

  const { data, error, loading, refetch } = useQueryWithErrorLogging(ClientPortalAccountsListQuery, {
    variables: queryVariables,
    skip: skipReads,
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: historyData,
    error: historyError,
    loading: historyQueryLoading,
    refetch: refetchHistory,
  } = useQueryWithErrorLogging(ClientPortalNetWorthHistoryQuery, {
    variables: queryVariables,
    skip: skipReads,
    notifyOnNetworkStatusChange: true,
  });

  const { data: freshnessData, refetch: refetchFreshness } = useQueryWithErrorLogging(
    ClientPortalAccountsFreshnessQuery,
    { variables: queryVariables, skip: skipReads },
  );

  const rowOrder = useAccountRowOrder(activeHouseholdId ?? user?.id ?? null);
  const groups = accountGroups(data?.portfolio).map((group) => ({
    ...group,
    rows: applyRowOrder(group.rows, rowOrder.order[group.id]),
  }));
  const stale = staleConnections(freshnessData?.portfolio);

  const [deleteAccount] = useMutationWithErrorLogging(ClientPortalDeleteAccountMutation);
  const [deleteAsset] = useMutationWithErrorLogging(ClientPortalDeleteAssetMutation);
  const [deleteLiability] = useMutationWithErrorLogging(ClientPortalDeleteLiabilityMutation);
  const [updateAccountCustomName] = useMutationWithErrorLogging(ClientPortalUpdateAccountCustomNameMutation);
  const [updateAccountNetWorth] = useMutationWithErrorLogging(ClientPortalUpdateAccountNetWorthMutation);
  const [updateAssetNetWorth] = useMutationWithErrorLogging(ClientPortalUpdateAssetNetWorthMutation);
  const [updateLiabilityNetWorth] = useMutationWithErrorLogging(ClientPortalUpdateLiabilityNetWorthMutation);

  async function deleteRow(row: AccountRow) {
    const variables = { input: { id: row.id } };
    if (row.kind === 'asset') {
      await deleteAsset({ variables });
      return;
    }
    if (row.kind === 'liability') {
      await deleteLiability({ variables });
      return;
    }
    await deleteAccount({ variables });
  }

  async function writeHiddenFromNetWorth(row: AccountRow, hiddenFromNetWorth: boolean) {
    const variables = { input: { id: row.id, hiddenFromNetWorth } };
    if (row.kind === 'asset') {
      await updateAssetNetWorth({ variables });
      return;
    }
    if (row.kind === 'liability') {
      await updateLiabilityNetWorth({ variables });
      return;
    }
    await updateAccountNetWorth({ variables });
  }

  return {
    loading: !canQuery || isNil(user?.id) || (loading && groups.length === 0),
    failed: !isNil(error),
    retry: () => {
      void refetch();
    },
    groups,
    netWorth: netWorthFromGroups(groups),
    balancesUpdatedLabel: balancesUpdatedLabel(balancesUpdatedAt(groups)),
    historyGroups: historyData?.portfolio.portfolioItemGroups,
    historyLoading: skipReads || historyQueryLoading,
    historyFailed: !isNil(historyError),
    retryHistory: () => {
      void refetchHistory().catch(() => undefined);
    },
    netWorthReturns,
    netWorthReturnsLoading,
    canConnect: canWrite,
    connectItemType,
    connectFeedOnly,
    connectOpen,
    openConnect: (itemType?: AddAccountItemType, feedOnly?: boolean) => {
      setConnectItemType(itemType);
      setConnectFeedOnly(feedOnly === true);
      setConnectOpen(true);
    },
    closeConnect: () => {
      setConnectOpen(false);
    },
    onConnected: () => {
      setConnectOpen(false);
      return refetch().catch(() => undefined);
    },
    staleConnections: undismissedConnections(stale, dismissals),
    dismissStaleConnections: (reason: StaleConnectionReason) => {
      const next = dismissalsWith(dismissals, stale, reason);
      writeDismissals(next);
      setDismissals(next);
    },
    onReconnected: () => Promise.all([refetch(), refetchFreshness()]).catch(() => undefined),
    transferring,
    destinationCustodian,
    showConnectNudge: shouldShowConnectNudge({
      transferring,
      canConnect: canWrite,
      dismissed: connectNudgeDismissed,
      portfolio: data?.portfolio,
    }),
    dismissConnectNudge,
    canManageRows: canWrite,
    dropRowOnto: rowOrder.dropRowOnto,
    nudgeRow: rowOrder.nudgeRow,
    refreshBalances: () => {
      void refetch()
        .then(() => {
          toast.success(ACCOUNTS_REFRESHED_TOAST);
        })
        .catch(() => undefined);
    },
    renamingRow,
    pendingCustomNameId,
    openRename: (row: AccountRow) => {
      setRenamingRow(row);
    },
    closeRename: () => {
      setRenamingRow(null);
    },
    renameRow: async (row: AccountRow, customName: string | null) => {
      setPendingCustomNameId(row.id);
      try {
        await updateAccountCustomName({
          variables: {
            input:
              customName === null
                ? { id: row.id, deleteCustomName: true }
                : { id: row.id, customName, deleteCustomName: false },
          },
        });
        await refetch().catch(() => undefined);
        toast.success(customName === null ? ACCOUNTS_RENAME_CLEARED_TOAST : ACCOUNTS_RENAMED_TOAST);
      } catch (mutationError: unknown) {
        handleMutationError(mutationError, ACCOUNTS_RENAME_ERROR_TOAST);
      } finally {
        setPendingCustomNameId(null);
      }
    },
    balanceRow,
    openBalanceUpdate: (row: AccountRow) => {
      setBalanceRow(row);
    },
    closeBalanceUpdate: () => {
      setBalanceRow(null);
    },
    onBalanceUpdated: () => {
      setBalanceRow(null);
      toast.success(ACCOUNTS_BALANCE_UPDATED_TOAST);
      void Promise.all([refetch().catch(() => undefined), refetchHistory().catch(() => undefined)]);
    },
    removeRow: async (row: AccountRow) => {
      try {
        await deleteRow(row);
      } catch (mutationError: unknown) {
        handleMutationError(mutationError, ACCOUNTS_REMOVE_ERROR_TOAST);
        throw mutationError;
      }
      toast.success(ACCOUNTS_REMOVED_TOAST);
      await refetch().catch(() => undefined);
    },
    toggleRowInNetWorth: async (row: AccountRow) => {
      const nextHidden = row.countsTowardNetWorth;
      try {
        await writeHiddenFromNetWorth(row, nextHidden);
      } catch (mutationError: unknown) {
        handleMutationError(mutationError, ACCOUNTS_NET_WORTH_ERROR_TOAST);
        throw mutationError;
      }
      toast.success(nextHidden ? ACCOUNTS_HIDDEN_FROM_NET_WORTH_TOAST : ACCOUNTS_INCLUDED_IN_NET_WORTH_TOAST);
      await Promise.all([refetch().catch(() => undefined), refetchHistory().catch(() => undefined)]);
    },
  };
}
